import "react-big-calendar/lib/css/react-big-calendar.css";
import 'moment/locale/ko';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import styled from 'styled-components';

import Loader from '../../components/Loader';
import Button from '../../components/Button';
import useAxios from '../../hooks/useAxios';
import ErrorResponse from '../../components/ErrorResponse';
import { getHolidays } from '../../api/holidays.api';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import DeleteHolidayModal from '../../components/program/holiday/modals/DeleteHolidayModal';
import CreateHolidayModal from "../../components/program/holiday/modals/CreateHolidayModal";
import { IGetHolidays } from "../../models/holidays.model";
import moment from 'moment';
import SimpleHeader from '../../components/layout/SimpleHeader';
import PrevBtn from '../../../public/images/btn_pre2.svg';
import NextBtn from '../../../public/images/btn_next2.svg';

moment.locale('ko');
const localizer = momentLocalizer(moment);

const Block = styled.div`
  .arrow > * {
    font-size: 26px;
    line-height: 26px;
  }

  .date {
    flex-grow: 0;
    margin: 0px 20px;
    font-size: 24px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    text-align: left;
    color: #222;
  }
`;

export const CustomCalendar = styled(Calendar)`
    
    .rbc-month-row {
        overflow: initial;    
    }
    
    .rbc-row.rbc-month-header {
        & > .rbc-header {
            display: flex;
            justify-content: center;
            align-items: center;
            background-color: #fff;
            color: #2e4052;
            font-size: 12px;
            border-top: 1px solid #2e4052;
            border-bottom: 1px solid #eaecee;
            height: 41px;
        }
    }
    .rbc-row-content-scrollable .rbc-row-content-scroll-container {
        scrollbar-width: none;
        overflow-y: initial;
        height: initial;
        padding-bottom: 5px;
        
        & .rbc-row-segment {
            padding: 1px 8.5px;
            margin-bottom: 1px;
        }
    }
    
    .rbc-row-content > .rbc-row {
        height: 50px;
        
        & > .rbc-row-segment {
            height: 28px;
            padding: 0 8.5px;
            font-weight: 600;
            
            & > .rbc-event.rbc-event-allday {
                display: flex;
                align-items: center;
                justify-content: center;
                height: 100%;
                background-color: #2e4052;
            }

            & > .rbc-event.rbc-event-allday:not(:has(> .rbc-event-content[title~="방학"])) {
                background-color: #17a2b8;
            }
        }
    }
    
    .rbc-row > .rbc-date-cell:first-child, .rbc-date-cell:last-child > a {
        color: #999
    }

    .rbc-row > .rbc-date-cell {
        height: 50px;
        position: relative;
        & > a {
            display: flex;
            align-items: center;
            justify-content: center;
            position: absolute;
            top: 4.5px;
            right: 20px;
            width: 14px;
            height: 41px;
        }
    }
    
    .rbc-date-cell.rbc-now {
        position: relative;
        
        & > a {
            right: 6.5px;
            padding: 20px;
            border-radius: 50%;
            color: #fff;
            background-color: #8bd0db;
        }
    }
    .rbc-date-cell.rbc-now:before {
        content: 'Today';
        left: 20px;
        top: 14px;
        position: absolute;
        font-size: 12px;
        font-weight: bold;
        color: #17a2b8;
    }
    
    .rbc-month-row .rbc-day-bg.rbc-today {
        background-color: #fff !important;
    }
`

const ProgramHoliday: FC = () => {
  const { t, i18n } = useTranslation();
  const { serverDate } = useSelector((state: RootState) => state.serverDate);
  const { data: res, isLoading, error, execute } = useAxios<IGetHolidays>();

  const [events, setEvents] = useState([]);
  const [year, setYear] = useState<number>(0);
  const [month, setMonth] = useState<number>(0);
  const [day, setDay] = useState<number>(0);
  const [date, setDate] = useState<any>();
  const [createHolidayVisible, setCreateHolidayVisible] = useState(false);
  const [holidayDate, setHolidayDate] = useState('');
  const [deleteHolidayVisible, setDeleteHolidayVisible] = useState(false);
  const [event, setEvent] = useState<any>();

  useEffect(() => {
    doGetHolidays();
  }, []);

  useEffect(() => {
    if (res) {
      const _events: any = [];
      res.data.forEach((item: any) => {
        const year = Number(item.holidayDate.slice(0, 4));
        const month = Number(item.holidayDate.slice(4, 6)) - 1;
        const day = Number(item.holidayDate.slice(6));
        _events.push({
          id: item.holidayCode,
          title: item.holidayName,
          allDay: true,
          start: new Date(year, month, day),
          end: new Date(year, month, day + 1)
        })
      })
      setEvents(_events);
    }
  }, [res]);

  useEffect(() => {
    if (serverDate) {
      const _date = moment(serverDate);
      setYear(Number(_date.format('YYYY')));
      setMonth(Number(_date.format('M')) - 1);
      setDay(Number(_date.format('D')));
      setDate(_date);
    }
  }, [serverDate]);

  const doGetHolidays = () => execute(getHolidays());

  const setToday = () => {
    setYear(Number(date.format('YYYY')));
    setMonth(Number(date.format('M')) - 1);
    setDay(Number(date.format('D')));
  }

  const setPrevMonth = () => {
    if (month === 0) {
      setYear(year - 1);
      setMonth(11);
    } else {
      setMonth(month - 1);
    }
    setDay(1);
  }

  const setNextMonth = () => {
    if (month === 11) {
      setYear(year + 1);
      setMonth(0);
    } else {
      setMonth(month + 1);
    }
    setDay(1);
  }

  const onSelectEvent = (e: any) => {
    setEvent(e);
    setDeleteHolidayVisible(true);
  }

  const onSelectSlot = (slotInfo: any) => {
    setHolidayDate(slotInfo.start);
    setCreateHolidayVisible(true);
  }

  return (
    <>
      <div className="content scrollbar">
        {isLoading && <Loader />}
        <ErrorResponse error={error} />
        <SimpleHeader
          iconFile="icon-program-big.svg"
          parentMenu={t('common.menu.program')}
          currentMenu={t('program.holiday.title1')}
          onlyTitle
        />
        <div className="mt-3 flex">
          <div className="col-2 pl-0">
            <Button
              className="btn-basic pink-line"
              onClick={setToday}
              text={t('program.holiday.button1')}
            />
          </div>
          <Block className="col-8 text-center">
            <h5 className="d-inline-flex align-items-center">
              <span className="arrow mr-3 pointer">
                <img src={PrevBtn} onClick={setPrevMonth} alt={'이전버튼'}/>
              </span>
              <span className="date">
                {
                  i18n.language === 'ko'
                    ? `${year} · ${(month + 1).toString().length > 1 ? '' : 0}${month + 1}`
                    : `${month + 1}·${year}`
                }
              </span>
              <span className="arrow ml-3 pointer">
                <img src={NextBtn} onClick={setNextMonth} alt={'다음버튼'} />
              </span>
            </h5>
          </Block>
          <div className="col-2" />
        </div>
        <div>
          <CustomCalendar
            localizer={localizer}
            culture={i18n.language === 'ko' ? 'ko-KR' : 'en-US'}
            events={events}
            date={new Date(year, month, day)}
            onSelectEvent={e => onSelectEvent(e)}
            selectable
            onSelectSlot={slotInfo => {
              const holiday = res?.data?.find((holiday: any) => holiday.holidayDate === moment(slotInfo.start).format('YYYYMMDD'));
              if (holiday) {
                onSelectEvent({
                  id: holiday.holidayCode,
                  title: holiday.holidayName,
                  start: holiday.holidayDate,
              });
              } else {
                const day = moment(slotInfo.start).day();
                if (day > 0 && day < 6) {
                  onSelectSlot(slotInfo);
                }
              }
            }}
            toolbar={false}
            style={{ height: '88vh', minHeight: 600, margin: '10px 0' }}
            dayPropGetter={date => {
              if (date.getDay() === 0 || date.getDay() === 6) {
                return {
                  style: {
                    background: '#fbfbfc',
                  }
                }
              }
              return {};
            }}
          />
        </div>

        {createHolidayVisible && (
          <CreateHolidayModal
            visible={createHolidayVisible}
            setVisible={setCreateHolidayVisible}
            holidayDate={holidayDate}
            doGetHolidays={doGetHolidays}
          />
        )}
        {deleteHolidayVisible && (
          <DeleteHolidayModal
            visible={deleteHolidayVisible}
            setVisible={setDeleteHolidayVisible}
            event={event}
            setEvent={setEvent}
            doGetHolidays={doGetHolidays}
          />
        )}
      </div>
    </>
  );
}

export default ProgramHoliday;
